"use client";

import React, { useEffect, useRef, useState } from "react";
import { Briefcase, Building2, CircleCheck, FileText, Scale, Shield } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  title: "RESPONSABILITÀ ENTI (231)\xA0",
  description: "DCLegal assiste società ed enti nella predisposizione e aggiornamento di Modelli 231 su misura, offrendo una consulenza mirata e orientata alla prevenzione dei rischi. I Professionisti di DCLegal svolgono il ruolo di Presidente e componente degli OdV.",
  icon: Briefcase
}, {
  title: "PRIVACY E E TUTELA DEI DATI",
  description: "DCLegal si distingue come punto di riferimento per le imprese offrendo consulenza specializzata nell’adeguamento alle normative europee in ambito protezione dei dati e nell’implementazione di strategie efficaci per la tutela della privacy e la sicurezza delle informazioni.",
  icon: FileText
}, {
  title: "INTELLIGENZA ARTIFICIALE (AI)",
  description: "DCLegal offre consulenza all’avanguardia anche in materia di Intelligenza Artificiale, aiutando le aziende a cogliere le opportunità offerte dalle nuove tecnologie nel rispetto della normativa vigente.",
  icon: Shield
}, {
  title: "DIRITTO CIVILE E CONTRATTUALISTICA",
  description: "Lo Studio DCLegal offre ai propri clienti assistenza continuativa su numerose problematiche di diritto civile ed in particolare in merito alla contrattualistica d’impresa assistendo i clienti nelle diverse fasi di negoziazione, redazione, revisione ed esecuzione di contratti e accordi commerciali.",
  icon: Scale
}, {
  title: "ATTIVITÀ GIUDIZIARIA",
  description: "Lo Studio DCLegal, mediante il proprio Team di professionisti con esperienze e competenze diversificate e anche grazie all’ampia rete di collaborazioni con professionisti esterni specializzati in diversi settori del diritto, è in grado di fornire assistenza giudiziaria in materia civile, commerciale, tributaria, penale e amministrativa.",
  icon: CircleCheck
}, {
  title: "HSE, CYBERSECURITY AUDIT E CERTIFICAZIONI",
  description: "Lo Studio DCLegal, attraverso la partnership con la società di consulenza Development Compliance Partner S.r.l., è in grado di assistere i propri clienti negli adempimenti in materia di HSE, nella esecuzione di audit e nel percorso volto all'ottenimento di certificazioni.",
  icon: Building2
}];
export default function CardGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="cardgrid" className="py-[120px] !bg-[hsl(0,0%,100%)]">
      <div className="container mx-auto px-4 mb-16">
        <h2 className="tracking-tight font-sans !font-light !text-6xl !text-[hsl(239,31%,33%)] !text-center">Consulenza legale integrata per la governance e la compliance d'impresa</h2>
      </div>
      <div ref={ref} className={cn("container mx-auto px-4 transition-all duration-1000 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0")}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((item, i) => <Link key={i} to="/ConsultingPage" data-index={i} className="group block outline-none">
              <Card className="text-card-foreground flex flex-col gap-6 rounded-xl py-6 h-full border-white/10 backdrop-blur-md transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-2xl group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background !bg-[hsl(239,31%,33%)]">
                <CardHeader className="pb-4">
                  <item.icon className="mb-4 h-8 w-8 text-ring transition-transform duration-300 group-hover:-translate-y-1" />
                  <CardTitle className="leading-none font-sans tracking-tight text-primary-foreground !text-lg !font-medium">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm leading-relaxed text-primary-foreground/70 !font-sans !text-justify">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </Link>)}
        </div>
      </div>
    </section>;
}